import { CircularProgress, InputAdornment } from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { FormikNumberField, useFormikEnhanced } from '@superdispatch/forms';
import { useValueObserver } from '@superdispatch/hooks';
import { Color, ColorDynamic } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { FormikProvider } from 'formik';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { useUpdateCarrierInfo } from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import styled from 'styled-components';
import {
  CarrierEditDTO,
  carrierEditSchema,
} from '../../data/dto/CarrierEditDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';

const CapacityTextField = styled(FormikNumberField)`
  .MuiOutlinedInput-root {
    background: ${ColorDynamic.White};
  }
`;

const largestTrailerCapacityIsValidAtom = atom(true);
export function useLargestTrailerCapacityIsValidValue() {
  return useAtomValue(largestTrailerCapacityIsValidAtom);
}

export function PostingLargestTrailerCapacityField() {
  const updateCarrierInfo = useUpdateCarrierInfo();
  const setFormIsValid = useSetAtom(largestTrailerCapacityIsValidAtom);

  const formik = useFormikEnhanced<CarrierEditDTO, APIResponse>({
    initialValues: { largest_trailer_capacity: null },
    validationSchema: carrierEditSchema,
    onSubmit: (values) => updateCarrierInfo(values),
    onSubmitSuccess: (_, values) => {
      trackLoadboardEvent({
        name: 'Carrier Updated Carrier Info',
        largest_trailer_capacity: values.largest_trailer_capacity ?? undefined,
        utm_medium: 'Load Details',
      });
    },
  });

  useValueObserver(formik.isValid, () => {
    setFormIsValid(formik.isValid);
  });

  return (
    <FormikProvider value={formik}>
      <Box padding="xsmall" backgroundColor="Blue50" borderRadius="small">
        <CapacityTextField
          name="largest_trailer_capacity"
          fullWidth={true}
          disabled={formik.isSubmitting}
          onBlur={() => {
            formik.handleSubmit();
          }}
          label="Largest Trailer Capacity"
          helperText="Max. number of sedans your largest trailer can fit. (e.g. Honda Accord)"
          inputProps={{ decimalScale: 0, maxLength: 2 }}
          InputProps={{
            endAdornment: formik.isSubmitting ? (
              <InputAdornment position="end">
                <CircularProgress size={12} color="primary" />
              </InputAdornment>
            ) : formik.status.type === 'submitted' ? (
              <InputAdornment position="end">
                <CheckCircle fontSize="small" htmlColor={Color.Green300} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
    </FormikProvider>
  );
}
